import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Cloud, AlertTriangle, CheckCircle, XCircle, FileText, Moon, Wind, Waves, Eye, ChevronDown, Check, Ship, BarChart3, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import velocityLogoFullColor from "@/assets/velocity-logo-full-color.png";

// ─── NUMBER FORMATTING ─────────────────────────────────────
const fmt = (n: number) => (n ?? 0).toLocaleString("en-US");
const fmtD = (n: number, d = 2) => (n ?? 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });

const VO_GREEN = "#1D9E75";
const VO_RED = "#C53030";
const VO_NAVY = "#1D2F5C";
const VO_AMBER = "#D97706";
const VO_BLUE = "#2563EB";

// ─── OPERATIONAL LIMITS PER TRANSPORT TYPE ──────────────────
type OpLimits = {
  maxHsM: number;
  maxWindKts: number;
  nightOps: boolean;
  minVisKm: number;
  notes: string;
};

const HELI_LIMITS: Record<string, OpLimits> = {
  AW139:  { maxHsM: 4.5, maxWindKts: 45, nightOps: false, minVisKm: 3.0, notes: "Night ops restricted (NVG/risk assessment req.) · CAP 437 helideck limits · fog-sensitive" },
  S92:    { maxHsM: 5.0, maxWindKts: 50, nightOps: false, minVisKm: 3.0, notes: "Most capable heli · night ops still restricted per BARS · helideck motion limited" },
  H175:   { maxHsM: 4.5, maxWindKts: 45, nightOps: false, minVisKm: 3.0, notes: "Night ops restricted · Cat A · helideck pitch/roll limits apply" },
  AS332:  { maxHsM: 4.5, maxWindKts: 45, nightOps: false, minVisKm: 3.0, notes: "Super Puma · night ops require risk assessment per CAP 437 · fog-grounded" },
  AW189:  { maxHsM: 4.5, maxWindKts: 45, nightOps: false, minVisKm: 3.0, notes: "Night ops restricted · long range · helideck motion limited" },
  H160:   { maxHsM: 4.0, maxWindKts: 40, nightOps: false, minVisKm: 3.0, notes: "Lighter airframe · more wind-sensitive · night ops restricted" },
  S76D:   { maxHsM: 4.0, maxWindKts: 40, nightOps: false, minVisKm: 3.0, notes: "Medium twin · night ops restricted · helideck motion limited" },
};

const VESSEL_LIMITS: Record<string, OpLimits> = {
  aircat35:        { maxHsM: 2.5, maxWindKts: 55, nightOps: true,  minVisKm: 0.5, notes: "Catamaran hull · superior stability · radar + AIS · FMEA proven" },
  aircat35_combi:  { maxHsM: 2.5, maxWindKts: 55, nightOps: true,  minVisKm: 0.5, notes: "Phantom Series · twin-hull stability · radar + AIS · all-weather" },
  aircat36_combi:  { maxHsM: 2.5, maxWindKts: 55, nightOps: true,  minVisKm: 0.5, notes: "Apex Series · twin-hull stability · radar + AIS · all-weather" },
  gulf_craft_fsv:  { maxHsM: 3.0, maxWindKts: 50, nightOps: true,  minVisKm: 0.5, notes: "61m monohull · heavy displacement but single-hull roll" },
  ic0618:          { maxHsM: 2.5, maxWindKts: 45, nightOps: true,  minVisKm: 1.0, notes: "50m catamaran · moderate stability · less proven in harsh conditions" },
  ic013009:        { maxHsM: 2.5, maxWindKts: 45, nightOps: true,  minVisKm: 1.0, notes: "57m catamaran · moderate stability · conventional design" },
  mauric_fsh170:   { maxHsM: 1.5, maxWindKts: 35, nightOps: false, minVisKm: 2.0, notes: "Monohull · no DP · limited night ops · poor sea-keeping" },
  midship_marine_175: { maxHsM: 2.0, maxWindKts: 40, nightOps: true, minVisKm: 1.5, notes: "175ft monohull · DP2 · single-hull roll in beam seas" },
  piriou_seas175_conv: { maxHsM: 2.0, maxWindKts: 40, nightOps: true, minVisKm: 1.5, notes: "Conventional bow · DP2 · monohull roll limitations" },
  piriou_seas175_sb:   { maxHsM: 2.0, maxWindKts: 40, nightOps: true, minVisKm: 1.5, notes: "Straight bow · DP2 · monohull roll limitations" },
  surfer250:       { maxHsM: 1.5, maxWindKts: 35, nightOps: false, minVisKm: 2.0, notes: "25.95m · smaller hull · high motion in swells" },
  surfer2600:      { maxHsM: 1.5, maxWindKts: 35, nightOps: false, minVisKm: 2.0, notes: "25.95m · smaller hull · high motion in swells" },
  surfer320:       { maxHsM: 2.0, maxWindKts: 40, nightOps: true,  minVisKm: 1.5, notes: "33.5m · improved but still monohull limitations" },
  surfer320_combi: { maxHsM: 2.0, maxWindKts: 40, nightOps: true,  minVisKm: 1.5, notes: "34.33m combi · monohull · Fi-Fi capable" },
  surfer3600:      { maxHsM: 2.0, maxWindKts: 40, nightOps: true,  minVisKm: 1.5, notes: "36.3m · improved hull but still monohull design" },
};

// ─── REGIONAL WEATHER PRESETS ───────────────────────────────
type WeatherPreset = {
  name: string;
  region: string;
  hsM: number;
  windKts: number;
  visibility: "good" | "reduced" | "poor";
  nightOps: boolean;
  description: string;
  annualOpsWindowPct: number;
};

const WEATHER_PRESETS: WeatherPreset[] = [
  { name: "Guyana / Suriname", region: "Caribbean", hsM: 1.6, windKts: 20, visibility: "good", nightOps: true, description: "NE trade-wind swell · Hs 1.25–2.25m · benign year-round", annualOpsWindowPct: 97 },
  { name: "Gulf of Mexico (Calm)", region: "GoM", hsM: 1.0, windKts: 15, visibility: "good", nightOps: true, description: "Mild conditions · outside hurricane season", annualOpsWindowPct: 95 },
  { name: "Gulf of Mexico (Storm)", region: "GoM", hsM: 3.0, windKts: 45, visibility: "reduced", nightOps: false, description: "Tropical storm / hurricane season conditions", annualOpsWindowPct: 60 },
  { name: "North Sea (Summer)", region: "Europe", hsM: 2.0, windKts: 25, visibility: "good", nightOps: true, description: "N Atlantic · calmer summer window · Hs 1.5–2.5m", annualOpsWindowPct: 88 },
  { name: "North Sea (Winter)", region: "Europe", hsM: 4.0, windKts: 50, visibility: "poor", nightOps: true, description: "N Atlantic · harsh winter · Hs 3.0–5.0m+", annualOpsWindowPct: 65 },
  { name: "West Africa (Benign)", region: "West Africa", hsM: 1.25, windKts: 15, visibility: "good", nightOps: true, description: "Gulf of Guinea · equatorial · very calm", annualOpsWindowPct: 98 },
  { name: "West Africa (SW Swell)", region: "West Africa", hsM: 2.0, windKts: 25, visibility: "good", nightOps: true, description: "Angola/Nigeria · moderate SW Atlantic swell", annualOpsWindowPct: 93 },
  { name: "Persian Gulf", region: "Middle East", hsM: 0.5, windKts: 12, visibility: "good", nightOps: true, description: "Sheltered enclosed sea · very calm year-round", annualOpsWindowPct: 99 },
  { name: "SE Asia (Monsoon)", region: "Asia Pacific", hsM: 2.5, windKts: 35, visibility: "reduced", nightOps: true, description: "South China Sea · monsoon season", annualOpsWindowPct: 75 },
  { name: "SE Asia (Calm)", region: "Asia Pacific", hsM: 1.0, windKts: 15, visibility: "good", nightOps: true, description: "Inter-monsoon · benign conditions", annualOpsWindowPct: 96 },
  { name: "NW Shelf Australia", region: "Asia Pacific", hsM: 2.5, windKts: 35, visibility: "good", nightOps: true, description: "Indian Ocean swell · cyclone season Dec–Apr", annualOpsWindowPct: 82 },
  { name: "Brazil (Santos/Campos)", region: "South America", hsM: 2.0, windKts: 25, visibility: "good", nightOps: true, description: "SE Atlantic swell · moderate year-round", annualOpsWindowPct: 93 },
];

type WeatherCondition = {
  hsM: number;
  windKts: number;
  visibility: "good" | "reduced" | "poor";
  nightOps: boolean;
};

const VIS_KM: Record<string, number> = { good: 5.0, reduced: 2.0, poor: 0.5 };

type OpStatus = "go" | "marginal" | "no-go";

function assessOperability(weather: WeatherCondition, limits: OpLimits): { status: OpStatus; reasons: string[]; opsReduction: number } {
  const reasons: string[] = [];
  let reduction = 0;
  if (weather.hsM > limits.maxHsM) { reasons.push(`Hs ${weather.hsM}m exceeds ${limits.maxHsM}m limit`); reduction += 100; }
  else if (weather.hsM > limits.maxHsM * 0.8) { reasons.push(`Hs ${weather.hsM}m approaching ${limits.maxHsM}m limit`); reduction += 15; }
  if (weather.windKts > limits.maxWindKts) { reasons.push(`Wind ${weather.windKts}kts exceeds ${limits.maxWindKts}kts limit`); reduction += 100; }
  else if (weather.windKts > limits.maxWindKts * 0.8) { reasons.push(`Wind ${weather.windKts}kts approaching ${limits.maxWindKts}kts limit`); reduction += 10; }
  const visKm = VIS_KM[weather.visibility];
  if (visKm < limits.minVisKm) { reasons.push(`Visibility ${weather.visibility} (${visKm}km) below ${limits.minVisKm}km minimum`); reduction += 100; }
  else if (visKm < limits.minVisKm * 1.5) { reasons.push(`Reduced visibility — caution`); reduction += 10; }
  if (weather.nightOps && !limits.nightOps) { reasons.push("Night operations not supported"); reduction += 30; }
  const status: OpStatus = reduction >= 100 ? "no-go" : reduction > 0 ? "marginal" : "go";
  return { status, reasons, opsReduction: Math.min(100, reduction) };
}

function calcEffectiveOpsDays(baseOpsDays: number, opsReduction: number): number {
  return Math.round(baseOpsDays * (1 - opsReduction / 100));
}

const StatusBadge = ({ status }: { status: OpStatus }) => {
  if (status === "go") return <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300"><CheckCircle className="w-3 h-3" />GO</span>;
  if (status === "marginal") return <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300"><AlertTriangle className="w-3 h-3" />MARGINAL</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full bg-red-100 text-red-700 border border-red-300"><XCircle className="w-3 h-3" />NO-GO</span>;
};

type LimitCheckState = "pass" | "warn" | "fail";

const getLimitCheckState = (actual: number, limit: number, nearRatio = 0.8): LimitCheckState => {
  if (actual > limit) return "fail";
  if (actual > limit * nearRatio) return "warn";
  return "pass";
};

const getVisibilityCheckState = (visibility: WeatherCondition["visibility"], minVisKm: number): LimitCheckState => {
  const visKm = VIS_KM[visibility];
  if (visKm < minVisKm) return "fail";
  if (visKm < minVisKm * 1.5) return "warn";
  return "pass";
};

const limitStateClass: Record<LimitCheckState, string> = {
  pass: "text-emerald-600",
  warn: "text-amber-600",
  fail: "text-red-600 font-bold",
};

const limitStateIcon: Record<LimitCheckState, string> = {
  pass: "✓",
  warn: "⚠",
  fail: "✗",
};

const limitStateLabel: Record<LimitCheckState, string> = {
  pass: "OK",
  warn: "WARN",
  fail: "FAIL",
};

type HeliData = { name: string; pax: number; kts: number; burnGalHr: number; maintDayFlat: number; hourlyRate: number };
type VesselData = { name: string; pax: number; kts: number; mgoBurnMTHr: number; maintHr: number; dayRate: number; dp: string; loaM: number };
type BlockData = { name: string; fieldName?: string; nm: number; meanHs: number; avail: number; seaDesc: string; region: string };

interface WeatherOperabilityProps {
  heliKeys: string[];
  vesselKeys: string[];
  helis: Record<string, HeliData>;
  vessels: Record<string, VesselData>;
  blocks: BlockData[];
  selectedIdxs: number[];
  heliRateOverrides: Record<string, number>;
  vesselDayRateOverrides: Record<string, number>;
  opsDays: number;
  mgoPriceMT: number;
  jetFuelPerGal: number;
  aircatDayRate: number;
  aircatPax: number;
  aircatSpeedKts: number;
  aircatMgoBurnMTHr: number;
  aircatMaintHr: number;
  totalPax: number;
  fieldPaxOverrides: Record<number, number>;
}

const DraftInput = ({ value, onChange, min, max, step, suffix, className }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; suffix?: string; className?: string }) => {
  const [draft, setDraft] = useState(String(value));
  const prev = useRef(value);
  if (value !== prev.current) { prev.current = value; if (String(value) !== draft) setDraft(String(value)); }
  return (
    <div className="flex items-center gap-1">
      <input type="number" value={draft}
        onChange={e => { setDraft(e.target.value); const n = parseFloat(e.target.value); if (!isNaN(n)) { prev.current = n; onChange(n); } }}
        onBlur={() => { const n = parseFloat(draft); if (isNaN(n) || draft === "") { const f = min ?? 0; setDraft(String(f)); prev.current = f; onChange(f); } else { const c = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, n)); if (c !== n) { setDraft(String(c)); prev.current = c; onChange(c); } } }}
        min={min} max={max} step={step}
        className={className ?? "w-20 px-2 py-1.5 border border-border rounded-md text-sm bg-card text-foreground font-mono text-right focus:border-secondary focus:outline-none"}
      />
      {suffix && <span className="text-xs text-muted-foreground font-mono">{suffix}</span>}
    </div>
  );
};

// ── Multi-select dropdown ──
const MultiSelectDropdown = ({ label, icon, items, selectedKeys, onToggle, allLabel }: {
  label: string; icon: React.ReactNode; items: { key: string; label: string; sub?: string }[];
  selectedKeys: string[]; onToggle: (key: string) => void; allLabel?: string;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg border border-border bg-card hover:bg-accent/10 text-foreground transition-colors w-full"
      >
        {icon}
        <span className="truncate flex-1 text-left">
          {selectedKeys.length === items.length ? (allLabel ?? `All ${label}`) : selectedKeys.length === 0 ? `Select ${label}` : `${selectedKeys.length} ${label} selected`}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-40 bg-card border border-border rounded-lg shadow-xl max-h-64 overflow-y-auto w-72">
          <div className="p-2 border-b border-border flex items-center justify-between">
            <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">{label}</span>
            <button onClick={() => { const allSelected = selectedKeys.length === items.length; items.forEach(it => { if (allSelected ? selectedKeys.includes(it.key) : !selectedKeys.includes(it.key)) onToggle(it.key); }); }}
              className="text-[10px] font-medium text-secondary hover:underline"
            >{selectedKeys.length === items.length ? "Deselect All" : "Select All"}</button>
          </div>
          {items.map(it => {
            const checked = selectedKeys.includes(it.key);
            return (
              <button key={it.key} onClick={() => onToggle(it.key)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-accent/10 border-b border-border/30 last:border-none ${checked ? "bg-secondary/5" : ""}`}
              >
                <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-secondary border-secondary" : "border-border"}`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-foreground truncate">{it.label}</div>
                  {it.sub && <div className="text-[10px] text-muted-foreground truncate">{it.sub}</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
const WeatherOperability = ({
  heliKeys: parentHeliKeys, vesselKeys: parentVesselKeys, helis, vessels, blocks,
  selectedIdxs: parentSelectedIdxs,
  heliRateOverrides, vesselDayRateOverrides, opsDays, mgoPriceMT, jetFuelPerGal,
  aircatDayRate, aircatPax, aircatSpeedKts, aircatMgoBurnMTHr, aircatMaintHr,
  totalPax, fieldPaxOverrides,
}: WeatherOperabilityProps) => {
  // ── Local selections (independent of comparator tab) ──
  const [localFieldIdxs, setLocalFieldIdxs] = useState<number[]>(parentSelectedIdxs);
  const [localHeliKeys, setLocalHeliKeys] = useState<string[]>(Object.keys(HELI_LIMITS));
  const [localVesselKeys, setLocalVesselKeys] = useState<string[]>(Object.keys(VESSEL_LIMITS));

  // Sync when parent changes
  useEffect(() => { setLocalFieldIdxs(prev => prev.length === 0 ? parentSelectedIdxs : prev); }, [parentSelectedIdxs]);

  const toggleFieldIdx = useCallback((idx: number) => {
    setLocalFieldIdxs(prev => prev.includes(idx) ? (prev.length <= 1 ? prev : prev.filter(i => i !== idx)) : [...prev, idx]);
  }, []);
  const toggleHeli = useCallback((key: string) => {
    setLocalHeliKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }, []);
  const toggleVessel = useCallback((key: string) => {
    setLocalVesselKeys(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  }, []);

  const [weatherByField, setWeatherByField] = useState<Record<number, WeatherCondition>>(() => {
    const init: Record<number, WeatherCondition> = {};
    blocks.forEach((b, idx) => {
      init[idx] = { hsM: b.meanHs, windKts: b.meanHs > 2.0 ? 30 : 20, visibility: "good", nightOps: true };
    });
    return init;
  });

  const [reportOpen, setReportOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);

  const getWeather = useCallback((idx: number): WeatherCondition => {
    return weatherByField[idx] ?? { hsM: blocks[idx]?.meanHs ?? 1.5, windKts: 20, visibility: "good" as const, nightOps: true };
  }, [weatherByField, blocks]);

  const updateWeather = useCallback((idx: number, patch: Partial<WeatherCondition>) => {
    setWeatherByField(prev => ({ ...prev, [idx]: { ...prev[idx] ?? { hsM: 1.5, windKts: 20, visibility: "good" as const, nightOps: true }, ...patch } }));
  }, []);

  const applyPreset = useCallback((preset: WeatherPreset) => {
    const updated: Record<number, WeatherCondition> = {};
    localFieldIdxs.forEach(idx => {
      updated[idx] = { hsM: preset.hsM, windKts: preset.windKts, visibility: preset.visibility, nightOps: preset.nightOps };
    });
    setWeatherByField(prev => ({ ...prev, ...updated }));
    setPresetOpen(false);
  }, [localFieldIdxs]);

  // Build dropdown items
  const fieldItems = useMemo(() => blocks.map((b, idx) => ({
    key: String(idx), label: b.fieldName || b.name, sub: `${b.nm}nm · ${b.region}`
  })), [blocks]);

  const heliItems = useMemo(() => Object.entries(HELI_LIMITS).map(([key, lim]) => ({
    key, label: helis[key]?.name ?? key, sub: `Max Hs ${lim.maxHsM}m · ${lim.maxWindKts}kts · ${lim.nightOps ? "Night ✓" : "Day only"}`
  })), [helis]);

  const vesselItems = useMemo(() => {
    const aircatItems = Object.entries(VESSEL_LIMITS)
      .filter(([k]) => k.startsWith("aircat"))
      .map(([key, lim]) => ({
        key, label: `⚡ ${vessels[key]?.name ?? key}`, sub: `Max Hs ${lim.maxHsM}m · ${lim.maxWindKts}kts · ${lim.nightOps ? "Night ✓" : "Day only"}`
      }));
    const otherItems = Object.entries(VESSEL_LIMITS)
      .filter(([k]) => !k.startsWith("aircat"))
      .map(([key, lim]) => ({
        key, label: vessels[key]?.name ?? key, sub: `Max Hs ${lim.maxHsM}m · ${lim.maxWindKts}kts · ${lim.nightOps ? "Night ✓" : "Day only"}`
      }));
    return [...aircatItems, ...otherItems];
  }, [vessels]);

  // ── Compute operability matrix ──
  const matrix = useMemo(() => {
    return localFieldIdxs.map(idx => {
      const b = blocks[idx];
      if (!b) return null;
      const weather = getWeather(idx);
      const pax = fieldPaxOverrides[idx] ?? totalPax;

      const heliOps = localHeliKeys.map(hk => {
        const limits = HELI_LIMITS[hk] ?? { maxHsM: 6.0, maxWindKts: 60, nightOps: true, minVisKm: 0.8, notes: "" };
        const assessment = assessOperability(weather, limits);
        const effDays = calcEffectiveOpsDays(opsDays, assessment.opsReduction);
        const h = helis[hk];
        if (!h) return null;
        return { key: hk, name: h.name, type: "heli" as const, assessment, effDays, limits };
      }).filter(Boolean) as any[];

      // AIRCAT vessels (always included as "Velocity fleet")
      const aircatKeys = localVesselKeys.filter(k => k.startsWith("aircat"));
      const aircatOps = aircatKeys.map(ak => {
        const limits = VESSEL_LIMITS[ak];
        const assessment = assessOperability(weather, limits);
        const effDays = calcEffectiveOpsDays(opsDays, assessment.opsReduction);
        const v = vessels[ak];
        const name = v?.name ? `${v.name} (Velocity)` : "AIRCAT 35 (Velocity)";
        return { key: ak, name, type: "aircat" as const, assessment, effDays, limits };
      });

      // If no aircat keys selected but we still want AIRCAT 35 baseline
      const defaultAircatOps = aircatKeys.length === 0 ? (() => {
        const limits = VESSEL_LIMITS.aircat35;
        const assessment = assessOperability(weather, limits);
        const effDays = calcEffectiveOpsDays(opsDays, assessment.opsReduction);
        return [{ key: "aircat35", name: "AIRCAT 35 (Velocity)", type: "aircat" as const, assessment, effDays, limits }];
      })() : [];

      const competitorVesselKeys = localVesselKeys.filter(k => !k.startsWith("aircat"));
      const vesselOps = competitorVesselKeys.map(vk => {
        const limits = VESSEL_LIMITS[vk] ?? { maxHsM: 2.5, maxWindKts: 35, nightOps: true, minVisKm: 1.5, notes: "" };
        const assessment = assessOperability(weather, limits);
        const effDays = calcEffectiveOpsDays(opsDays, assessment.opsReduction);
        const v = vessels[vk];
        if (!v) return null;
        return { key: vk, name: v.name, type: "vessel" as const, assessment, effDays, limits };
      }).filter(Boolean) as any[];

      const allAircats = [...aircatOps, ...defaultAircatOps];
      const allOps = [...heliOps, ...allAircats, ...vesselOps];

      // AIRCAT advantage: purely operability-based (effective days, night ops)
      const bestAircat = allAircats.filter(a => a.assessment.status !== "no-go").sort((a: any, b: any) => b.effDays - a.effDays)[0];
      const competitorOps = [...heliOps, ...vesselOps].filter(o => o.assessment.status !== "no-go");
      const aircatAdvantage = bestAircat ? {
        extraOpsDays: bestAircat.effDays - (competitorOps.filter((c: any) => c.type === "vessel").length > 0 ? Math.max(...competitorOps.filter((c: any) => c.type === "vessel").map((c: any) => c.effDays), 0) : 0),
        nightOpsCapable: bestAircat.limits.nightOps,
        competitorsWithoutNight: vesselOps.filter((v: any) => !v.limits.nightOps).length,
        helisWithoutNight: heliOps.filter((h: any) => !h.limits.nightOps).length,
        aircatStatus: bestAircat.assessment.status,
        competitorNoGo: vesselOps.filter((v: any) => v.assessment.status === "no-go").length,
        heliNoGo: heliOps.filter((h: any) => h.assessment.status === "no-go").length,
      } : null;

      return { idx, block: b, weather, heliOps, aircatOps: allAircats, vesselOps, allOps, aircatAdvantage, bestAircat };
    }).filter(Boolean) as any[];
  }, [localFieldIdxs, localHeliKeys, localVesselKeys, weatherByField, helis, vessels, blocks, opsDays, getWeather]);

  // Chart data
  const chartData = useMemo(() => {
    if (matrix.length === 0) return [];
    const first = matrix[0];
    return first.allOps.map((op: any) => ({
      name: op.name.length > 18 ? op.name.slice(0, 16) + "…" : op.name,
      fullName: op.name,
      effDays: op.effDays,
      status: op.assessment.status,
      isAircat: op.type === "aircat",
      fill: op.type === "aircat" ? VO_GREEN : op.assessment.status === "no-go" ? "#DC2626" : op.assessment.status === "marginal" ? VO_AMBER : VO_BLUE,
    }));
  }, [matrix]);

  // Aggregate AIRCAT advantage across all fields
  const aggregateAdvantage = useMemo(() => {
    const advantages = matrix.filter((m: any) => m.aircatAdvantage);
    if (advantages.length === 0) return null;
    const avgExtraDays = advantages.reduce((s: number, m: any) => s + m.aircatAdvantage.extraOpsDays, 0) / advantages.length;
    const totalCompetitorNoGo = advantages.reduce((s: number, m: any) => s + m.aircatAdvantage.competitorNoGo, 0);
    const totalHeliNoGo = advantages.reduce((s: number, m: any) => s + m.aircatAdvantage.heliNoGo, 0);
    const totalCompetitorsWithoutNight = advantages.reduce((s: number, m: any) => s + m.aircatAdvantage.competitorsWithoutNight, 0);
    const fieldsWhereAircatGo = matrix.filter((m: any) => m.bestAircat?.assessment.status === "go").length;
    return { avgExtraDays, totalCompetitorNoGo, totalHeliNoGo, totalCompetitorsWithoutNight, fieldsWhereAircatGo, totalFields: matrix.length };
  }, [matrix]);

  return (
    <div className="space-y-6">
      {/* ── SELECTION PANEL ── */}
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-secondary" />
          <h3 className="font-display text-base font-semibold text-foreground">Analysis Configuration</h3>
          <span className="text-[10px] text-muted-foreground ml-auto font-mono">
            {localFieldIdxs.length} field{localFieldIdxs.length !== 1 ? "s" : ""} · {localHeliKeys.length} helicopter{localHeliKeys.length !== 1 ? "s" : ""} · {localVesselKeys.length} vessel{localVesselKeys.length !== 1 ? "s" : ""}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Select the fields, helicopters, and vessels you want to analyse. Weather conditions can be set per field below.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <MultiSelectDropdown
            label="Fields"
            icon={<Cloud className="w-3.5 h-3.5 text-secondary" />}
            items={fieldItems}
            selectedKeys={localFieldIdxs.map(String)}
            onToggle={k => toggleFieldIdx(parseInt(k))}
            allLabel="All Fields"
          />
          <MultiSelectDropdown
            label="Helicopters"
            icon={<span className="text-xs">🚁</span>}
            items={heliItems}
            selectedKeys={localHeliKeys}
            onToggle={toggleHeli}
            allLabel="All Helicopters"
          />
          <MultiSelectDropdown
            label="Vessels"
            icon={<Ship className="w-3.5 h-3.5 text-secondary" />}
            items={vesselItems}
            selectedKeys={localVesselKeys}
            onToggle={toggleVessel}
            allLabel="All Vessels"
          />
        </div>
      </div>

      {/* ── WEATHER CONDITIONS INPUT ── */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cloud className="w-5 h-5 text-secondary" />
            <h3 className="font-display text-base font-semibold text-foreground">Weather Conditions</h3>
          </div>
          <div className="relative">
            <button onClick={() => setPresetOpen(!presetOpen)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-accent/10 text-foreground transition-colors"
            >Apply Regional Preset ▾</button>
            {presetOpen && (
              <div className="absolute right-0 top-full mt-1 z-30 bg-card border border-border rounded-lg shadow-xl max-h-80 overflow-y-auto w-80">
                <div className="p-2 border-b border-border">
                  <span className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground">Regional Weather Profiles</span>
                </div>
                {WEATHER_PRESETS.map((p, i) => (
                  <button key={i} onClick={() => applyPreset(p)}
                    className="w-full text-left px-3 py-2 hover:bg-accent/10 border-b border-border/50 last:border-none"
                  >
                    <div className="text-sm font-medium text-foreground">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground">Hs {p.hsM}m · Wind {p.windKts}kts · Vis {p.visibility} · {p.nightOps ? "Night OK" : "Day only"} · ~{p.annualOpsWindowPct}% ops window</div>
                    <div className="text-[10px] text-muted-foreground/60 italic">{p.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-4">Enter the weather conditions for each field/block to see which transport modes can operate and how it affects cost-effectiveness.</p>

        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {localFieldIdxs.map(idx => {
            const b = blocks[idx];
            if (!b) return null;
            const w = getWeather(idx);
            return (
              <div key={idx} className="rounded-lg border border-border p-4 bg-accent/5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-semibold text-foreground">{b.fieldName || b.name}</span>
                  <span className="text-[10px] text-muted-foreground font-mono">{b.nm}nm · {b.region}</span>
                  <span className="text-[10px] text-muted-foreground italic">Baseline: Hs {b.meanHs}m</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground mb-1.5"><Waves className="w-3 h-3" />Sig. Wave Height (Hs)</label>
                    <DraftInput value={w.hsM} onChange={v => updateWeather(idx, { hsM: v })} min={0} max={10} step={0.25} suffix="m" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground mb-1.5"><Wind className="w-3 h-3" />Wind Speed</label>
                    <DraftInput value={w.windKts} onChange={v => updateWeather(idx, { windKts: v })} min={0} max={100} step={5} suffix="kts" />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground mb-1.5"><Eye className="w-3 h-3" />Visibility</label>
                    <select value={w.visibility} onChange={e => updateWeather(idx, { visibility: e.target.value as any })}
                      className="w-full px-2 py-1.5 border border-border rounded-md text-sm bg-card text-foreground focus:border-secondary focus:outline-none"
                    >
                      <option value="good">Good (5+ km)</option>
                      <option value="reduced">Reduced (2 km)</option>
                      <option value="poor">Poor (&lt;1 km)</option>
                    </select>
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground mb-1.5"><Moon className="w-3 h-3" />Night Operations</label>
                    <select value={w.nightOps ? "yes" : "no"} onChange={e => updateWeather(idx, { nightOps: e.target.value === "yes" })}
                      className="w-full px-2 py-1.5 border border-border rounded-md text-sm bg-card text-foreground focus:border-secondary focus:outline-none"
                    >
                      <option value="yes">Required</option>
                      <option value="no">Not Required</option>
                    </select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── AIRCAT ADVANTAGE BANNER ── */}
      {aggregateAdvantage && (aggregateAdvantage.avgExtraDays > 0 || aggregateAdvantage.fieldsWhereAircatGo > 0) && (
        <div className="bg-gradient-to-r from-emerald-50 via-teal/5 to-secondary/10 border-2 border-teal/40 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-teal" />
            <span className="text-sm font-bold tracking-wide text-teal">AIRCAT Operational Advantage</span>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Extra Ops Days</div>
              <div className="text-2xl font-bold font-mono" style={{ color: VO_GREEN }}>+{Math.round(aggregateAdvantage.avgExtraDays)}</div>
              <div className="text-[10px] text-muted-foreground">avg vs competitor vessels</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">AIRCAT GO Status</div>
              <div className="text-2xl font-bold font-mono" style={{ color: VO_GREEN }}>{aggregateAdvantage.fieldsWhereAircatGo}/{aggregateAdvantage.totalFields}</div>
              <div className="text-[10px] text-muted-foreground">fields fully operable</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Competitors No-Go</div>
              <div className="text-2xl font-bold font-mono" style={{ color: VO_RED }}>{aggregateAdvantage.totalCompetitorNoGo}</div>
              <div className="text-[10px] text-muted-foreground">vessel modes grounded</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">No Night Ops</div>
              <div className="text-2xl font-bold font-mono" style={{ color: VO_RED }}>{aggregateAdvantage.totalCompetitorsWithoutNight}</div>
              <div className="text-[10px] text-muted-foreground">competitors restricted at night</div>
            </div>
          </div>
        </div>
      )}

      {/* ── GO / NO-GO MATRIX per field ── */}
      {matrix.map(({ idx, block, weather, allOps, aircatAdvantage, bestAircat }: any) => (
        <div key={idx} className="bg-card rounded-xl border border-border overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-accent/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground">Operability Matrix</span>
              <span className="text-xs font-semibold text-foreground">— {block.fieldName || block.name}</span>
            </div>
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span>Hs {weather.hsM}m</span><span>·</span><span>Wind {weather.windKts}kts</span><span>·</span>
              <span>Vis {weather.visibility}</span><span>·</span><span>{weather.nightOps ? "Night req." : "Day only"}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="text-left px-4 py-2 font-semibold">Transport Mode</th>
                  <th className="text-center px-3 py-2 font-semibold">Status</th>
                  <th className="text-center px-3 py-2 font-semibold">Max Hs</th>
                  <th className="text-center px-3 py-2 font-semibold">Max Wind</th>
                  <th className="text-center px-3 py-2 font-semibold">Night Ops</th>
                  <th className="text-center px-3 py-2 font-semibold">Min Vis</th>
                  <th className="text-right px-3 py-2 font-semibold">Eff. Days</th>
                  <th className="text-left px-3 py-2 font-semibold">Issues</th>
                </tr>
              </thead>
              <tbody>
                {allOps.map((op: any) => {
                  const isAircat = op.type === "aircat";
                  const hsState = getLimitCheckState(weather.hsM, op.limits.maxHsM);
                  const windState = getLimitCheckState(weather.windKts, op.limits.maxWindKts);
                  const visState = getVisibilityCheckState(weather.visibility, op.limits.minVisKm);
                  const nightPass = !weather.nightOps || op.limits.nightOps;
                  return (
                    <tr key={op.key} className={`border-b border-border/50 ${isAircat ? "bg-emerald-50/60" : op.assessment.status === "no-go" ? "bg-red-50/30 opacity-60" : "hover:bg-accent/5"}`}>
                      <td className="px-4 py-2.5 font-medium text-foreground">
                        <div className="flex items-center gap-1.5">
                          <span>{op.type === "heli" ? "🚁" : isAircat ? "⚡" : "⛵"}</span>
                          <span className={isAircat ? "font-bold" : ""}>{op.name}</span>
                        </div>
                      </td>
                      <td className="text-center px-3 py-2.5"><StatusBadge status={op.assessment.status} /></td>
                      <td className="text-center px-3 py-2.5 font-mono">
                        <span className={`inline-flex items-center gap-1 ${limitStateClass[hsState]}`}>
                          {limitStateIcon[hsState]} {op.limits.maxHsM}m
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5 font-mono">
                        <span className={`inline-flex items-center gap-1 ${limitStateClass[windState]}`}>
                          {limitStateIcon[windState]} {op.limits.maxWindKts}kts
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5">
                        <span className={`inline-flex items-center gap-1 font-semibold ${nightPass ? "text-emerald-600" : "text-red-600"}`}>
                          {nightPass ? "✓" : "✗"} {op.limits.nightOps ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="text-center px-3 py-2.5 font-mono">
                        <span className={`inline-flex items-center gap-1 ${limitStateClass[visState]}`}>
                          {limitStateIcon[visState]} {op.limits.minVisKm}km
                        </span>
                      </td>
                      <td className="text-right px-3 py-2.5 font-mono font-medium">
                        {op.assessment.status === "no-go" ? <span className="text-red-600">0</span> : <span>{op.effDays}</span>}
                        <span className="text-muted-foreground">/{opsDays}</span>
                      </td>
                      <td className="px-3 py-2.5 text-[10px] text-muted-foreground max-w-[200px]">
                        {op.assessment.reasons.length > 0 ? op.assessment.reasons.join(" · ") : "All clear"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* AIRCAT advantage per field */}
          {aircatAdvantage && bestAircat && (
            <div className="px-4 py-3 bg-gradient-to-r from-emerald-50 to-teal/5 border-t border-teal/20">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-teal" />
                <span className="text-sm font-semibold text-foreground">
                  AIRCAT Status: <span style={{ color: bestAircat.assessment.status === "go" ? VO_GREEN : bestAircat.assessment.status === "marginal" ? VO_AMBER : VO_RED }}>{bestAircat.assessment.status.toUpperCase()}</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  — {bestAircat.effDays} effective ops days
                </span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-[10px]">
                <span className="font-bold text-teal">⚡ AIRCAT Edge:</span>
                {aircatAdvantage.extraOpsDays > 0 && <span className="text-emerald-700">+{aircatAdvantage.extraOpsDays} more ops days vs competitors</span>}
                {aircatAdvantage.competitorsWithoutNight > 0 && <span className="text-emerald-700">{aircatAdvantage.competitorsWithoutNight} competitor(s) can't do night ops</span>}
                {aircatAdvantage.helisWithoutNight > 0 && <span className="text-emerald-700">All {aircatAdvantage.helisWithoutNight} helicopters restricted at night</span>}
                {aircatAdvantage.competitorNoGo > 0 && <span className="text-red-600">{aircatAdvantage.competitorNoGo} competitor vessel(s) grounded</span>}
              </div>
              {allOps.filter((o: any) => o.assessment.status === "no-go").length > 0 && (
                <div className="text-[10px] text-red-600 mt-1">
                  ⚠ {allOps.filter((o: any) => o.assessment.status === "no-go").length} transport mode(s) cannot operate in these conditions
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* ── CHART: Effective Operating Days ── */}
      {chartData.length > 0 && (
        <div className="bg-card rounded-xl border border-border p-6">
          <div className="text-[11px] font-bold tracking-widest uppercase text-muted-foreground mb-4">Effective Operating Days (out of {opsDays})</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 8 }} stroke="hsl(var(--muted-foreground))" interval={0} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" domain={[0, opsDays]} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v: number, _: string, props: any) => [`${v} days (${props.payload.status})`, props.payload.fullName]} />
              <Bar dataKey="effDays" radius={[6, 6, 0, 0]}>
                {chartData.map((entry: any, i: number) => <Cell key={i} fill={entry.fill} stroke={entry.isAircat ? VO_GREEN : "transparent"} strokeWidth={entry.isAircat ? 2 : 0} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── OPERABILITY SUMMARY ── */}
      <div className="bg-gradient-to-r from-teal/5 to-secondary/10 border border-teal/30 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Cloud className="w-4 h-4 text-teal" />
          <span className="text-[11px] font-bold tracking-widest uppercase text-teal/80">Weather Operability Summary</span>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Fully Operable</div>
            <div className="text-2xl font-bold font-mono text-emerald-600">
              {matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === "go").length, 0)}
            </div>
            <div className="text-[10px] text-muted-foreground">transport modes across {matrix.length} field(s)</div>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Marginal</div>
            <div className="text-2xl font-bold font-mono text-amber-600">
              {matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === "marginal").length, 0)}
            </div>
            <div className="text-[10px] text-muted-foreground">reduced effective ops days</div>
          </div>
          <div>
            <div className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">No-Go</div>
            <div className="text-2xl font-bold font-mono text-red-600">
              {matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === "no-go").length, 0)}
            </div>
            <div className="text-[10px] text-muted-foreground">cannot operate in conditions</div>
          </div>
        </div>
      </div>

      {/* Generate Report Button */}
      <div className="text-center py-4">
        <button onClick={() => setReportOpen(true)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-navy-deep text-primary-foreground rounded-xl text-sm font-medium hover:bg-navy-light transition-colors shadow-lg"
        >
          <FileText className="w-4 h-4" />
          Generate Weather Operability Report
        </button>
      </div>

      {/* ── REPORT MODAL ── */}
      {reportOpen && (
        <WeatherReport matrix={matrix} opsDays={opsDays} aggregateAdvantage={aggregateAdvantage} onClose={() => setReportOpen(false)} />
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════
// WEATHER REPORT MODAL
// ═══════════════════════════════════════════════════════════
const WeatherReport = ({ matrix, opsDays, aggregateAdvantage, onClose }: {
  matrix: any[];
  opsDays: number;
  aggregateAdvantage: any;
  onClose: () => void;
}) => {
  const date = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const handlePrint = () => {
    document.body.classList.add("report-print-open");
    setTimeout(() => { window.print(); document.body.classList.remove("report-print-open"); }, 100);
  };

  const handlePdfDownload = () => {
    import('jspdf').then(({ default: jsPDF }) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pw = doc.internal.pageSize.getWidth();
        const margin = 15;
        let y = 15;
        const addPage = () => { doc.addPage(); y = 15; };
        const checkPage = (need: number) => { if (y + need > 275) addPage(); };

        // Header
        doc.setFillColor(29, 47, 92);
        doc.rect(0, 0, pw, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Weather Operability Analysis', margin, 14);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Velocity Offshore Holdings LLC', margin, 20);
        doc.setFontSize(8);
        doc.text(`Date: ${date} | Fields: ${matrix.length} | Base ops days: ${opsDays}`, margin, 28);
        doc.text('CONFIDENTIAL', pw - margin, 28, { align: 'right' });
        y = 42;

        // AIRCAT Advantage Summary
        if (aggregateAdvantage && (aggregateAdvantage.avgExtraDays > 0 || aggregateAdvantage.fieldsWhereAircatGo > 0)) {
          doc.setFillColor(225, 245, 238);
          doc.roundedRect(margin, y, pw - margin * 2, 16, 2, 2, 'F');
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(15, 110, 86);
          doc.text('⚡ AIRCAT OPERATIONAL ADVANTAGE', margin + 4, y + 6);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.text(`+${Math.round(aggregateAdvantage.avgExtraDays)} extra ops days avg | GO in ${aggregateAdvantage.fieldsWhereAircatGo}/${aggregateAdvantage.totalFields} fields | ${aggregateAdvantage.totalCompetitorNoGo} competitor No-Go instances | ${aggregateAdvantage.totalCompetitorsWithoutNight} without night ops`, margin + 4, y + 12);
          y += 22;
        }

        // Per-field analysis
        matrix.forEach((m: any) => {
          checkPage(80);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(29, 47, 92);
          doc.text(`${m.block.fieldName || m.block.name}`, margin, y);
          y += 5;
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(80, 80, 80);
          doc.text(`Weather: Hs ${m.weather.hsM}m | Wind ${m.weather.windKts}kts | Vis ${m.weather.visibility} | Night ${m.weather.nightOps ? "Required" : "Not req."}`, margin, y);
          y += 6;

          (doc as any).autoTable({
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Transport Mode', 'Status', 'Max Hs', 'Max Wind', 'Night', 'Min Vis', 'Eff. Days', 'Issues']],
            body: m.allOps.map((op: any) => {
              const hsState = getLimitCheckState(m.weather.hsM, op.limits.maxHsM);
              const windState = getLimitCheckState(m.weather.windKts, op.limits.maxWindKts);
              const visState = getVisibilityCheckState(m.weather.visibility, op.limits.minVisKm);
              const nightPass = !m.weather.nightOps || op.limits.nightOps;
              return [
                op.name,
                op.assessment.status.toUpperCase(),
                `${limitStateLabel[hsState]} · ${op.limits.maxHsM}m`,
                `${limitStateLabel[windState]} · ${op.limits.maxWindKts}kts`,
                `${nightPass ? 'OK' : 'FAIL'} · ${op.limits.nightOps ? 'Yes' : 'No'}`,
                `${limitStateLabel[visState]} · ${op.limits.minVisKm}km`,
                op.assessment.status === 'no-go' ? '0' : `${op.effDays}/${opsDays}`,
                op.assessment.reasons.join('; ') || 'All clear',
              ];
            }),
            headStyles: { fillColor: [29, 47, 92], fontSize: 6.5 },
            bodyStyles: { fontSize: 6.5 },
            theme: 'grid',
            didParseCell: (data: any) => {
              if (data.section === 'body') {
                if (data.column.index === 1) {
                  const val = data.cell.raw;
                  if (val === 'GO') data.cell.styles.textColor = [22, 163, 74];
                  else if (val === 'MARGINAL') data.cell.styles.textColor = [217, 119, 6];
                  else if (val === 'NO-GO') data.cell.styles.textColor = [220, 38, 38];
                }
                const name = data.row.raw?.[0] ?? '';
                if (typeof name === 'string' && name.includes('Velocity')) {
                  data.cell.styles.fillColor = [225, 245, 238];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            },
          });
          y = (doc as any).lastAutoTable.finalY + 4;

          if (m.bestAircat) {
            doc.setFillColor(225, 245, 238);
            doc.roundedRect(margin, y, pw - margin * 2, 10, 2, 2, 'F');
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(15, 110, 86);
            doc.text(`AIRCAT STATUS: ${m.bestAircat.assessment.status.toUpperCase()} — ${m.bestAircat.effDays} effective ops days${m.aircatAdvantage?.extraOpsDays > 0 ? ` | +${m.aircatAdvantage.extraOpsDays} days vs competitors` : ''}`, margin + 3, y + 7);
            y += 14;
          }
          y += 6;
        });

        // Summary
        checkPage(30);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(29, 47, 92);
        doc.text('Operability Summary', margin, y);
        y += 6;

        const totalGo = matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === 'go').length, 0);
        const totalMarginal = matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === 'marginal').length, 0);
        const totalNoGo = matrix.reduce((s: number, m: any) => s + m.allOps.filter((o: any) => o.assessment.status === 'no-go').length, 0);

        (doc as any).autoTable({
          startY: y,
          margin: { left: margin, right: margin },
          head: [['Category', 'Count', 'Description']],
          body: [
            ['Fully Operable (GO)', totalGo, 'All conditions within operational limits'],
            ['Marginal', totalMarginal, 'Approaching limits — reduced effective ops days'],
            ['No-Go', totalNoGo, 'One or more limits exceeded — cannot operate'],
          ],
          headStyles: { fillColor: [29, 47, 92], fontSize: 7 },
          bodyStyles: { fontSize: 7 },
          theme: 'grid',
        });
        y = (doc as any).lastAutoTable.finalY + 8;

        // Disclaimer
        checkPage(20);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(120, 120, 120);
        const disclaimer = 'This analysis is based on client-provided weather conditions and standardized operational limits. Actual operability depends on vessel-specific equipment, crew competency, flag state regulations, and real-time sea conditions. This report is for indicative planning purposes only.';
        const lines = doc.splitTextToSize(disclaimer, pw - margin * 2);
        doc.text(lines, margin, y);

        // Footer on every page
        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFillColor(29, 47, 92);
          doc.rect(0, 285, pw, 12, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.text('Velocity Offshore Holdings LLC — CONFIDENTIAL', margin, 291);
          doc.text(`VOH-WOA-RPT — ${date} — Page ${i}/${totalPages}`, pw - margin, 291, { align: 'right' });
        }

        doc.save(`Velocity_Weather_Operability_${date.replace(/\s/g, '_')}.pdf`);
      });
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/50 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div className="max-w-[860px] mx-auto my-8 bg-white rounded-xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
        <div style={{ background: VO_NAVY, color: "#fff", padding: "28px 32px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
            <img src={velocityLogoFullColor} alt="Velocity Offshore" style={{ height: 48, width: "auto" }} />
            <div style={{ fontSize: 13, opacity: 0.7, borderLeft: "1px solid rgba(255,255,255,0.3)", paddingLeft: 16 }}>Weather Operability Analysis</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: 12 }}>
            <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Report Type</strong>Weather Operability Analysis</div>
            <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Date</strong>{date}</div>
            <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Fields Analysed</strong>{matrix.length} field(s)</div>
            <div style={{ opacity: 0.85 }}><strong style={{ display: "block", opacity: 0.6, fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>Prepared By</strong>Velocity Offshore Holdings LLC</div>
          </div>
        </div>

        {/* AIRCAT Advantage banner in report */}
        {aggregateAdvantage && (aggregateAdvantage.avgExtraDays > 0 || aggregateAdvantage.fieldsWhereAircatGo > 0) && (
          <div style={{ background: "#E1F5EE", padding: "16px 32px", borderBottom: "2px solid #1D9E75" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>⚡</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: VO_GREEN }}>AIRCAT Operational Advantage</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, fontSize: 12 }}>
              <div><div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>Extra Ops Days</div><div style={{ fontSize: 18, fontWeight: 700, color: VO_GREEN }}>+{Math.round(aggregateAdvantage.avgExtraDays)}</div></div>
              <div><div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>AIRCAT GO</div><div style={{ fontSize: 18, fontWeight: 700, color: VO_GREEN }}>{aggregateAdvantage.fieldsWhereAircatGo}/{aggregateAdvantage.totalFields}</div></div>
              <div><div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>Competitors No-Go</div><div style={{ fontSize: 18, fontWeight: 700, color: VO_RED }}>{aggregateAdvantage.totalCompetitorNoGo}</div></div>
              <div><div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>No Night Ops</div><div style={{ fontSize: 18, fontWeight: 700, color: VO_RED }}>{aggregateAdvantage.totalCompetitorsWithoutNight}</div></div>
            </div>
          </div>
        )}

        <div style={{ padding: "20px 32px" }}>
          {matrix.map((m: any, fi: number) => (
            <div key={fi} style={{ marginBottom: 24, borderBottom: "1px solid #e5e4e0", paddingBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, color: VO_NAVY, marginBottom: 8 }}>{m.block.fieldName || m.block.name}</h3>
              <div style={{ fontSize: 11, color: "#666", marginBottom: 12 }}>
                Weather: Hs {m.weather.hsM}m · Wind {m.weather.windKts}kts · Visibility {m.weather.visibility} · Night ops {m.weather.nightOps ? "required" : "not required"}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #1D2F5C" }}>
                    <th style={{ textAlign: "left", padding: "6px 8px", color: VO_NAVY }}>Transport Mode</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Status</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Max Hs</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Max Wind</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Night</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Min Vis</th>
                    <th style={{ textAlign: "center", padding: "6px 8px", color: VO_NAVY }}>Eff. Days</th>
                    <th style={{ textAlign: "left", padding: "6px 8px", color: VO_NAVY }}>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {m.allOps.map((op: any, i: number) => {
                    const hsState = getLimitCheckState(m.weather.hsM, op.limits.maxHsM);
                    const windState = getLimitCheckState(m.weather.windKts, op.limits.maxWindKts);
                    const visState = getVisibilityCheckState(m.weather.visibility, op.limits.minVisKm);
                    const nightPass = !m.weather.nightOps || op.limits.nightOps;
                    const stateColor = (state: LimitCheckState) => state === "fail" ? "#DC2626" : state === "warn" ? "#D97706" : "#16A34A";
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #eee", background: op.type === "aircat" ? "#E1F5EE" : op.assessment.status === "no-go" ? "#FEF2F2" : "transparent" }}>
                        <td style={{ padding: "6px 8px", fontWeight: op.type === "aircat" ? 700 : 400 }}>
                          {op.type === "heli" ? "🚁" : op.type === "aircat" ? "⚡" : "⛵"} {op.name}
                        </td>
                        <td style={{ textAlign: "center", padding: "6px 8px", fontWeight: 700, color: op.assessment.status === "go" ? "#16A34A" : op.assessment.status === "marginal" ? "#D97706" : "#DC2626" }}>
                          {op.assessment.status.toUpperCase()}
                        </td>
                        <td style={{ textAlign: "center", padding: "6px 8px", fontFamily: "monospace", color: stateColor(hsState), fontWeight: hsState === "fail" ? 700 : 400 }}>{limitStateIcon[hsState]} {op.limits.maxHsM}m</td>
                        <td style={{ textAlign: "center", padding: "6px 8px", fontFamily: "monospace", color: stateColor(windState), fontWeight: windState === "fail" ? 700 : 400 }}>{limitStateIcon[windState]} {op.limits.maxWindKts}kts</td>
                        <td style={{ textAlign: "center", padding: "6px 8px", color: nightPass ? "#16A34A" : "#DC2626", fontWeight: 700 }}>{nightPass ? "✓" : "✗"} {op.limits.nightOps ? "Yes" : "No"}</td>
                        <td style={{ textAlign: "center", padding: "6px 8px", fontFamily: "monospace", color: stateColor(visState), fontWeight: visState === "fail" ? 700 : 400 }}>{limitStateIcon[visState]} {op.limits.minVisKm}km</td>
                        <td style={{ textAlign: "center", padding: "6px 8px", fontFamily: "monospace" }}>
                          {op.assessment.status === "no-go" ? "0" : op.effDays}/{opsDays}
                        </td>
                        <td style={{ padding: "6px 8px", fontSize: 10, color: "#888" }}>
                          {op.assessment.reasons.length > 0 ? op.assessment.reasons.join(" · ") : "All clear"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}

          <div style={{ fontSize: 10, color: "#888", fontStyle: "italic", marginTop: 16 }}>
            This analysis is based on client-provided weather conditions and standardized operational limits. Actual operability depends on vessel-specific equipment, crew competency, flag state regulations, and real-time sea conditions. This report is for indicative planning purposes only.
          </div>
        </div>

        <div style={{ background: VO_NAVY, color: "#fff", padding: "14px 32px", fontSize: 11, display: "flex", justifyContent: "space-between" }}>
          <span>Velocity Offshore Holdings LLC — CONFIDENTIAL</span>
          <span>VOH-WOA-RPT · {date}</span>
        </div>

        <div className="p-4 border-t border-border flex gap-3" style={{ background: "#f5f4f0" }}>
          <button onClick={handlePrint} style={{ padding: "7px 16px", background: VO_NAVY, color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>Print / Save as PDF</button>
          <button onClick={handlePdfDownload} style={{ padding: "7px 16px", background: VO_GREEN, color: "#fff", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>⬇ Download PDF</button>
          <button onClick={onClose} style={{ padding: "7px 16px", background: "#fff", border: "1px solid #d3d1c7", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default WeatherOperability;
